import { createUseStyles } from 'react-jss';

import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    expanded: {},
    notActive: {},
    mainWrapper:{
        display: 'flex',
        width: '100%',
        height: '100%',
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
    },

    header:{
        display: "flex",
        alignItems: "center",
        gap: 24,
        padding: 12,
        background: theme.colors.surface_alt,
        width: '100%',
        height: 55,
        flex: '0 0 55px',
        [theme.down('mobile')]: {
            gap: 12,
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
        },
    },

    stepWrap: {
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
        gap: 10,
        cursor: 'pointer',
        [theme.down('mobile')]: {
            flex: '0 0 auto',
        },
    },

    // The recap step is a different kind of destination (summary view), so it
    // reads as a pill rather than another numbered step.
    recapWrap: {
        display: "flex",
        alignItems: "center",
        flex: '0 0 auto',
        marginLeft: 'auto',
        gap: 8,
        padding: '5px 14px 5px 6px',
        borderRadius: 999,
        border: `1px solid ${theme.colors.border_subtle}`,
        background: theme.colors.white,
        cursor: 'pointer',
        transition: 'border-color .2s ease, background .2s ease',

        '&:hover': {
            borderColor: theme.colors.violet_primary,
        },

        '& $circle': {
            width: 24,
            height: 24,
            fontSize: 11,
        },
    },

    recapWrapActive: {
        borderColor: theme.colors.violet_primary,
        background: theme.colors.transp_violet,
    },

    circle: {
        width: 29,
        height: 29,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        fontWeight: 700,
        background: theme.colors.border_subtle,
        color: theme.colors.grey_text,
        flex: "0 0 auto",
    },

    label: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.colors.grey_text,
        whiteSpace: "nowrap",
        flex: "0 0 auto",
    },

    line: {
        height: 2,
        background: theme.colors.border_subtle,
        borderRadius: 999,
        flex: 1,
        minWidth: 12,
        [theme.down('mobile')]: {
            flex: '0 0 12px',
        },
    },

    activeCircle: { background: theme.colors.violet_primary, color: theme.colors.white },
    activeLabel: { color: theme.colors.violet_primary },
    notGradedCircle: {
        background: theme.colors.error_bg,
        color: theme.colors.error_text_dark,
    },
    notGradedLabel: {
        color: theme.colors.error_text_dark,
    },

    body: {
        display: 'flex',
        padding: '0 24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(4),
        flex: 1,
        minHeight: 0,
        alignSelf: 'stretch',
        borderRadius: 4,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    compDataWrapper:{
        display: 'flex',
        alignItems: 'stretch',
        gap: theme.spacing(2),
        alignSelf: 'stretch',
        minWidth: 0,
        flex: '0 0 auto',
    },
    competencyWrapper:{
        display: 'flex',
        width: 'auto',
        flex: '0 1 clamp(240px, 24vw, 340px)',
        minWidth: 220,
        maxWidth: 360,
        padding: 24,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 24,
        alignSelf: 'stretch',
        borderRadius: 4,
        backgroundColor: theme.colors.white,
    },
    compHeaderContainer:{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        alignSelf: 'stretch',
    },
    compTitleContainer:{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: '1 0 0',

        '& b':{
            color: theme.colors.text_heading,

            /* Title-Sub/Subtitle 1 */
            fontFamily: theme.font.secondary,
            fontSize: 18,
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
        },
    },
    compCounterContainer:{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,

        '& b':{
            color: theme.colors.text_heading,

            /* Title-Sub/Subtitle 1 */
            fontFamily: theme.font.secondary,
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
        },

        '& p':{
            color: theme.colors.text_heading,

            /* Title-Sub/Subtitle 1 */
            fontFamily: theme.font.secondary,
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: 'normal',
        },
    },

    compDescriptionContainer:{
        alignSelf: 'stretch',

        color: theme.colors.text_heading,

        /* Body/M-Regular */
        fontFamily: theme.font.primary,
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 1.5,
    },

    gradingContainer:{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,

        '& b':{
            color: theme.colors.text_heading,

            /* Body/M-Bold */
            fontFamily: theme.font.secondary,
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
        },
    },
    gradingValueContainer:{
        display: 'flex',
        height: 28,
        padding: '8px 12px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        borderRadius: 4,
    },
    noValue:{
        fontFamily: theme.font.secondary,
        fontStyle: 'italic',
        lineHeight: 'normal',
    },
    itemList: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
    },

    temRow: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        cursor: "pointer",

        "&:focus-visible": {
            outline: "2px solid rgba(91, 75, 183, 0.35)",
            outlineOffset: 2,
            borderRadius: 6,
        },

        "&$isChecked $temText": {
            color: theme.colors.text_heading,
        },

        "&$isChecked $temRadio": {
            borderColor: theme.colors.violet_primary,

            "&::after": {
                content: '""',
                position: "absolute",
                inset: 3,
                borderRadius: 999,
                background: theme.colors.violet_primary,
            },
        },
    },

    temRadio: {
        width: 16,
        height: 16,
        borderRadius: 999,
        border: `2px solid ${theme.colors.grey_darker}`,
        background: theme.colors.white,
        display: "inline-block",
        boxSizing: "border-box",
        position: "relative",
    },

    temText: {
        fontSize: 13,
        color: theme.colors.grey_text,
    },

    isChecked: {},

    notesContainer:{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        alignSelf: 'stretch',

        '& b':{
            color: theme.colors.text_heading,

            /* Body/M-Bold */
            fontFamily: theme.font.secondary,
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
        },
    },
    textInputContainer:{
        display: 'flex',
        height: 120,
        padding: 16,
        alignItems: 'flex-start',
        alignSelf: 'stretch',
    },
    textInput:{
        width: '100%',
        maxWidth: 440,
        minHeight: 92,
        padding: '10px 12px',

        fontSize: 13,
        lineHeight: '1.45',
        color: theme.colors.text_heading,
        background: theme.colors.white,

        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: 4,

        boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.03)',

        resize: 'both',
        outline: 'none',

        '$ :foucs':{
            borderColor: theme.colors.indigo_primary, /* light indigo */
            boxShadow:
                'inset 0 1px 0 rgba(0, 0, 0, 0.03), 0 0 0 2px rgba(99, 102, 241, 0.2)',
        },
    },

    behavioursContainer:{
        display: 'flex',
        padding: 24,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
        flex: '1 1 460px',
        minWidth: 0,
        alignSelf: 'stretch',
        borderRadius: 4,
        backgroundColor: theme.colors.white,
    },

    behaviourContainer:{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        borderRadius: 4,
        background: theme.colors.surface_page,
    },
    behaviourHeader:{
        display: 'flex',
        minHeight: 56,
        padding: '12px 16px',
        alignItems: 'center',
        gap: 16,
        alignSelf: 'stretch',
        border: 0,
        background: 'transparent',
        textAlign: 'left',
    },
    behaviourHeaderInteractive: {
        cursor: 'pointer',
    },
    behaviourCheckbox:{
        display: "flex",
        alignItems: "center",
        gap: 12,
    },
    titleContainer:{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: '1 0 0',
        minWidth: 0,
        fontFamily: theme.font.secondary,
        fontSize: 15,
        fontWeight: '700',
        lineHeight: 1.4,
        color: theme.colors.text_heading,
    },
    behaviourCounter:{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginLeft: 'auto',
    },
    behaviourCountBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 48,
        padding: '4px 8px',
        borderRadius: 999,
        background: theme.colors.surface_elevated,
        color: theme.colors.text_heading,
        fontFamily: theme.font.secondary,
        fontSize: 12,
        fontWeight: '700',
        lineHeight: 1,
    },
    behaviourToggleIcon: {
        width: 20,
        height: 20,
        flexShrink: 0,
    },

    behaviourDataContainer:{
        display: 'flex',
        padding: 12,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 8,
        alignSelf: 'stretch',
    },

    behaviourStepContainer:{
        display: 'flex',
        gap: 16,
        alignSelf: 'stretch',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 48,
        padding: '6px 8px',
        cursor: 'pointer',
        borderRadius: 4,

        '&:hover': {
            background: theme.colors.grey_light,
        },
    },

    obValueContainer:{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: '1 0 0',
        cursor: 'pointer',
        color: theme.colors.text_heading,
    },

    footer:{
        display: 'flex',
        flex: '0 0 auto',
        minHeight: 72,
        boxSizing: 'border-box',
        padding: '12px 24px 16px',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
    },

    cancelButton:{
        display: 'flex',
        height: 40,
        minWidth: 40,
        padding: '0 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        borderRadius: 4,
        background: 'rgba(233, 230, 239, 0.00)',
        borderWidth: 0,
        color: theme.colors.violet_primary,
        fontFamily: theme.font.secondary,

        '&:hover':{
            background: theme.colors.tab_white,
        },
    },

    selectorControls:{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    incompleteNotice: {
        fontFamily: theme.font.secondary,
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.error_text_dark,
        marginRight: 12,
    },
    backButton:{
        display: 'flex',
        height: 40,
        minWidth: 40,
        padding: '0 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        borderRadius: 4,
        background: theme.colors.tab_white,
        borderWidth: 0,
        color: theme.colors.violet_primary,
        fontFamily: theme.font.secondary,

        '&:hover':{
            background: theme.colors.tab_white,
        },
    },
    nextButton:{
        display: 'flex',
        height: 40,
        minWidth: 40,
        padding: '0 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        borderRadius: 4,
        background: theme.colors.violet_primary,
        borderWidth: 0,
        color: theme.colors.white,
        fontFamily: theme.font.secondary,

        '&:hover':{
            background: theme.colors.tab_white,
        },
    },

    ccWrapper: {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        cursor: "pointer",
        userSelect: "none",
        minWidth: 40,
        minHeight: 40,
        padding: 8,
        justifyContent: 'center',
        flexShrink: 0,
    },

    ccWrapperDisabled: {
        cursor: "default",
        opacity: 0.6,
    },

    ccInput: {
        position: "absolute",
        opacity: 0,
        pointerEvents: "none",
    },

    ccBox: {
        width: 22,
        height: 22,
        borderRadius: 4,
        border: "2px solid transparent",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        lineHeight: 1,
        fontWeight: 700,
        transition: "all 0.15s ease-in-out",
    },

    ccCompBoxChecked:{
        backgroundColor: theme.colors.transp_violet,
    },

    ccCompBoxUnchecked:{
        backgroundColor: theme.colors.red_transparent,
    },

    ccBoxChecked: {
        borderColor: theme.colors.violet_primary,
        backgroundColor: theme.colors.violet_primary,
        color: theme.colors.white,
    },

    ccBoxUnchecked: {
        borderColor: theme.colors.violet_primary,
        color: theme.colors.error_text_dark,
    },

    ccLabel: {
        fontSize: "0.95rem",
        color: theme.colors.text_heading,
    },

    ccWrapperFocusWithin: {
        "&:focus-within $ccBox": {
            outline: `0px solid ${theme.colors.indigo_primary}`,
            outlineOffset: 2,
        },
    },

    noDataContainer:{
        height: '100%',
        width: '100%',
        fontSize: 20,
        fontStyle: 'italic',
        textAlign: 'center',
        fontFamily: theme.font.secondary,
        alignContent: 'center',
    },

    [theme.down('tablet')]: {
        body: {
            padding: '0 12px',
            gap: theme.spacing(2),
        },
        compDataWrapper: {
            flexDirection: 'column',
            gap: theme.spacing(1.5),
        },
        competencyWrapper: {
            flex: '0 0 auto',
            width: '100%',
            minWidth: 0,
            maxWidth: 'none',
            padding: 16,
        },
        behavioursContainer: {
            flex: '0 0 auto',
            width: '100%',
            padding: 16,
        },
        compTitleContainer: {
            '& b': {
                fontSize: 16,
            },
        },
        compDescriptionContainer: {
            fontSize: 14,
        },
        ccWrapper: {
            minWidth: 44,
            minHeight: 44,
        },
        behaviourStepContainer: {
            minHeight: 52,
        },
    },
}));
